import { getCredentials } from '../cache/auth'
import { cartDetailsVar, totalQuantityVar } from '../cache/cart'
import { normalizeLanguageCode } from '../localization'
import { ROLE } from '../staticRoleManagement/types'
import { Currency } from '../types/currency'
import { EntityType } from '../types/reserveMonitoringList'
import { canonicalRole } from './canonicalRole'

export const getNameFromStorage = (): string | undefined => {
  const storageUser = getCredentials()

  return storageUser?.name
}

export const getEmailFromStorage = (): string | undefined => {
  const storageUser = getCredentials()

  return storageUser?.email
}

/** The role as IAM names it ('Channel_Admin'), for display. Compare with getRoleFromStorage. */
export const getRoleLabelFromStorage = (): string | undefined =>
  getCredentials()?.role

export const getRoleFromStorage = (): ROLE => {
  return canonicalRole(getCredentials()?.role) as ROLE
}

export const getEntityCode = (): string | undefined => {
  const storageUser = getCredentials()

  return storageUser?.entityCode
}

export const getEntityId = (): string | undefined => {
  const storageUser = getCredentials()

  return storageUser?.entityId
}

export const getEntityName = (): string | undefined => {
  const storageUser = getCredentials()

  return storageUser?.entityName
}

/**
 * The user's granted languages, in canonical form.
 *
 * Normalised on READ rather than on write: a write-side fix would correct new
 * records and leave every existing one broken, and the records are owned by IAM.
 * See `normalizeLanguageCode` for why the two sides disagree about case.
 *
 * `filter(Boolean)` guards a null entry inside the array — normalisation calls a
 * string method, so a null element would throw inside a render path where nothing
 * catches it. Dedupe follows normalisation because a record holding both `"en"`
 * and `"EN"` collapses to two identical codes, which would render as a duplicate
 * menu row with a duplicate React key.
 */
export const getLanguagesAvailable = (): string[] => {
  const storageUser = getCredentials()

  const languagesAvailable = storageUser?.regionalSettings?.languagesAvailable

  return Array.from(
    new Set(
      (languagesAvailable || []).filter(Boolean).map(normalizeLanguageCode)
    )
  )
}

export const isFaAvailableInLanguages = (): boolean => {
  const storageUser = getCredentials()

  // Before login, no credentials exist — allow all languages so the
  // /fa/login route is reachable.  After login the user's regional
  // settings control which languages are available.
  if (!storageUser) return true

  return getLanguagesAvailable().includes('FA')
}

export const getCurrenciesAvailable = (): Currency[] => {
  const storageUser = getCredentials()

  const currenciesAvailable = storageUser?.regionalSettings?.currenciesAvailable

  return currenciesAvailable || []
}

export const getDefaultCurrency = (): Currency => {
  const storageUser = getCredentials()

  const defaultCurrency = storageUser?.regionalSettings?.defaultCurrency

  return defaultCurrency
}

export const getTimeZone = (): string => {
  const storageUser = getCredentials()

  const timezone = storageUser?.regionalSettings?.timezone

  return timezone || ''
}
export const cleanCartData = () => {
  totalQuantityVar(0)
  localStorage.removeItem('totalQuantity')
  localStorage.removeItem('cartDetails')
  localStorage.removeItem('cartID')
  cartDetailsVar({
    propertyCode: '',
    currrncy: '',
    channelCode: '',
    channelID: '',
    channelName: ''
  })
}

export const findEntityTypeFromRole = (role: ROLE): EntityType => {
  switch (role) {
    case ROLE.TENANT:
    case ROLE.TENANT_MARKETING:
    case ROLE.TENANT_IT:
    case ROLE.TENANT_FINANCE:
    case ROLE.TENANT_MANAGEMENT:
    case ROLE.TENANT_PR:
      return 'Tenant'

    case ROLE.ACCOMMODATION:
    case ROLE.ACCOMMODATION_OPERATOR:
      return 'Accommodation'

    case ROLE.CHANNEL:
    case ROLE.CHANNEL_ADMIN:
      return 'Channel'

    case ROLE.PROVIDER:
    case ROLE.PROVIDER_IT:
    case ROLE.PROVIDER_MARKETING:
    case ROLE.PROVIDER_FINANCE:
    case ROLE.PROVIDER_MANAGEMENT:
    case ROLE.PROVIDER_PR:
      return 'Provider'

    default:
      return ''
  }
}
